// - - - - - - - - - -
// #194 — Labor-Catalog Attribute gate for the capacity recommender.
//
// Brandon's spec (issue #194, 2026-06-09):
//   "When jobs are recommended for an installer, we currently look at three
//    capacities: job square feet, # furnitures, # stairs … expand that to
//    Attributes. If a Labor Catalog item#/SKU has an Attribute associated with
//    it, and that item#/SKU is on the job, then the job is recommended ONLY IF
//    that Attribute is on the installer's Attributes list. If the SKU's
//    Attribute is NOT on the installer's list, do NOT recommend. If the
//    Attribute field in the labor catalog is blank/empty, ignore that SKU."
//
// Data model (verified live, 2026-06-09):
//   • LaborCatalog collection (manually maintained, like Stores): one row per
//     item#, fields `itemNo` (SKU, STRING — may be non-numeric e.g. "BM0098"
//     or have leading zeros, so join as a trimmed string, NEVER parseInt) and
//     `attribute` (single value; 176 of 467 rows populated, 291 blank).
//   • Candidates.LaborAttributes — the installer's CHECKED attributes, a
//     `\r`-separated string whose values are EXACTLY the catalog's `attribute`
//     strings (same FileMaker source; stale spellings were cleaned in #194).
//   • A job's SKUs live in InvoiceLineItems[i]["KI_AQ » InvoiceLineItems::sku"].
//
// Match is trimmed exact-string (case-insensitive avoided — both sides come
// from the same source). The gate is a no-op (allows everything) when the
// catalog map is empty/absent — the safe transition default, mirroring the
// #149 capacity_stores placeholder, so the frontend is safe to ship before/
// without the Lambda + data.

const LINE_ITEM_SKU_KEY = "KI_AQ » InvoiceLineItems::sku";

/**
 * Parse a Candidate's LaborAttributes (`\r`-separated string) into a Set of
 * trimmed attribute strings. Returns an empty Set for missing / non-string.
 */
export function parseInstallerLaborAttributes(candidate) {
  const raw = candidate && candidate.LaborAttributes;
  if (typeof raw !== "string") return new Set();
  return new Set(
    raw
      .split(/[\r\n]+/)
      .map((s) => s.trim())
      .filter(Boolean)
  );
}

/**
 * Build a lookup Map from LaborCatalog docs: itemNo (trimmed string) →
 * attribute (trimmed string). Only rows with a NON-EMPTY attribute are
 * included — blank-attribute SKUs never gate, so omitting them keeps the map
 * small and means "SKU not in map" === "no attribute requirement."
 */
export function buildLaborAttributeBySku(laborCatalogDocs) {
  const map = new Map();
  if (!Array.isArray(laborCatalogDocs)) return map;
  for (const d of laborCatalogDocs) {
    if (!d) continue;
    const sku =
      d.itemNo === undefined || d.itemNo === null ? "" : String(d.itemNo).trim();
    const attr = typeof d.attribute === "string" ? d.attribute.trim() : "";
    if (sku && attr) map.set(sku, attr);
  }
  return map;
}

/**
 * The distinct, sorted set of NON-EMPTY attribute values in the catalog — the
 * current "attribute categories." Drives CrewDetailModal's Attributes-tab
 * checkbox list so it's live from the catalog (single source of truth) instead
 * of a hardcoded array that drifts. Pure; case-insensitive locale sort.
 */
export function buildLaborAttributeOptions(laborCatalogDocs) {
  if (!Array.isArray(laborCatalogDocs)) return [];
  const set = new Set();
  for (const d of laborCatalogDocs) {
    const attr = d && typeof d.attribute === "string" ? d.attribute.trim() : "";
    if (attr) set.add(attr);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

/**
 * Gate predicate: does the installer have EVERY Labor-Catalog attribute that
 * this job's SKUs require?
 *
 * For each of the job's InvoiceLineItems SKUs, look it up in
 * `laborAttributeBySku`. If it maps to a (non-empty) attribute, the installer
 * must have that attribute in `installerAttributeSet`. ALL such required
 * attributes must be present (AND). SKUs with no catalog attribute are ignored.
 *
 * Safe defaults (return true = "allow / don't block"):
 *   • empty / absent catalog map → inert (pre-data behavior preserved)
 *   • job has no InvoiceLineItems → nothing to gate
 *   • none of the job's SKUs carry a catalog attribute → no requirement
 *
 * @param {Object} doc                    — ActiveQueue job doc
 * @param {Map}    laborAttributeBySku    — itemNo→attribute (buildLaborAttributeBySku)
 * @param {Set}    installerAttributeSet  — installer's checked attrs (parseInstallerLaborAttributes)
 * @returns {boolean}
 */
export function installerHasRequiredLaborAttributes(
  doc,
  laborAttributeBySku,
  installerAttributeSet
) {
  if (!laborAttributeBySku || laborAttributeBySku.size === 0) return true;
  if (!doc || !Array.isArray(doc.InvoiceLineItems)) return true;

  const required = new Set();
  for (const item of doc.InvoiceLineItems) {
    if (!item) continue;
    const skuRaw = item[LINE_ITEM_SKU_KEY];
    if (skuRaw === undefined || skuRaw === null) continue;
    const sku = String(skuRaw).trim();
    if (!sku) continue;
    const attr = laborAttributeBySku.get(sku);
    if (attr) required.add(attr);
  }
  if (required.size === 0) return true;

  const have = installerAttributeSet || new Set();
  for (const attr of required) {
    if (!have.has(attr)) return false;
  }
  return true;
}
